import { Fragment, HTMLProps, ReactNode, memo } from 'react';

import { flip, offset, shift } from '@floating-ui/react-dom';
import { ArrowBack, ChevronRight } from 'icons';
import { twMerge } from 'tailwind-merge';
import { Heading, IconButton } from 'ui/atoms';
import { Dropdown, type Option } from 'ui/molecules';

import { DropdownValueDivider } from '../../Dropdown/DropdownValueDivider';

export interface _AdminSettingsHeaderProps {
  dropDownOptions?: Array<Option>;
  dropdownButtonLabel?: ReactNode;
  // Value of the currently selected option — lets the Dropdown highlight and
  // display the active choice. Falls back to `dropdownButtonLabel` as placeholder.
  dropdownValue?: any;
  mainDirectoryTitle: ReactNode;
  subDirectoryTitles?: Array<ReactNode>;
  isBackButtonVisible?: boolean;
  onBackButtonClick?: () => void;
  additionalComponent?: ReactNode;
  bottomBorder?: boolean;
  truncateSubTitle?: boolean;
  className?: string;
}

export type AdminSettingsHeaderProps = HTMLProps<HTMLDivElement> & _AdminSettingsHeaderProps;

export function AdminSettingsHeader({
  mainDirectoryTitle,
  subDirectoryTitles,
  truncateSubTitle = true,
  isBackButtonVisible,
  onBackButtonClick,
  className,
  dropDownOptions,
  dropdownButtonLabel,
  dropdownValue,
  additionalComponent,
  bottomBorder = true,
}: _AdminSettingsHeaderProps) {
  return (
    <div
      data-testid="outvioui--adminsettingsheader"
      className={twMerge(
        'w-full flex items-center pl-4 py-5 sm:py-7 text-v2blueGray-900 font-inter text-[22px] leading-7 tracking-[-0.033em]',
        bottomBorder && 'border-b-[1px] border-v2blueGray-200',
        className,
      )}
    >
      {isBackButtonVisible && (
        <IconButton
          data-testid="outvioui--adminsettingsheader-nav-button"
          className="flex items-center justify-center w-9 h-9 mr-2"
          onClick={onBackButtonClick}
        >
          <ArrowBack width="24" height="24" />
        </IconButton>
      )}
      <Heading
        className="capitalize sm:text-[22px] sm:leading-[28px] sm:tracking-[-0.033em]"
        as="h1"
        type="heading2"
      >
        {mainDirectoryTitle}
      </Heading>
      <div className="flex h-9 items-center">
        {subDirectoryTitles &&
          subDirectoryTitles.map((title, index) => {
            const last = index === subDirectoryTitles.length - 1;

            return (
              <Fragment key={`${title}-${index}`}>
                <ChevronRight
                  width="20"
                  height="20"
                  className="ml-2 mr-[5px] text-v2blueGray-500"
                />
                <Heading
                  className={twMerge(last && truncateSubTitle && 'flex-1 truncate')}
                  as="span"
                  type="heading1"
                  weight="regular"
                >
                  {title}
                </Heading>
              </Fragment>
            );
          })}
      </div>

      {additionalComponent && <>{additionalComponent}</>}

      {dropDownOptions && dropdownButtonLabel && (
        <>
          <div className={additionalComponent ? '' : 'grow'} />
          <Dropdown
            Value={DropdownValueDivider}
            classNamePopup="min-w-[192px]"
            floatingOptions={{
              placement: 'bottom-end',
              middleware: [offset(4), flip(), shift()],
              transform: false,
              strategy: 'fixed',
            }}
            className="z-10 w-full max-w-[200px]"
            value={dropdownValue}
            placeholder={dropdownButtonLabel}
            onChange={(value) => {
              const option = dropDownOptions.find((o) => o.value === value);
              option?.onClick?.(option);
            }}
            options={dropDownOptions.map((x) => ({
              label: x.label,
              value: x.value,
              icon: x.icon,
            }))}
          />
        </>
      )}
    </div>
  );
}

export default memo(AdminSettingsHeader);
